import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import type { DualPathMutationResult } from '../dualPathMutationResult';
import type { FrontendRouterInputs } from '../routerInputs';

export interface UpdateModulesTRPCInput {
  ModuleSettings: FrontendRouterInputs['organisationModule']['update']['ModuleSettings'];
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: { affected_rows: number }) => ({
  insert_organisation_module: {
    __typename: 'organisation_module_mutation_response' as const,
    affected_rows: data.affected_rows,
  },
});

export type UpdateModulesResult = ReturnType<typeof mapTrpcResponseToGraphQL>;
export type UpdateModulesCompatibilityResult =
  DualPathMutationResult<UpdateModulesResult>;

export const useUpdateModulesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.organisationModule.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.organisationModule.getByOrgId.queryKey(),
        });
        // COI-889: module grants gate `checkNavigationVisibility`, cached for
        // 60 min with `refetchOnMount: false`.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.permission.pathKey(),
        });
      },
    }),
  });

  return {
    updateModules: async (
      input: UpdateModulesTRPCInput
    ): Promise<UpdateModulesResult> => {
      const result = await mutation.mutateAsync({
        ModuleSettings: input.ModuleSettings,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
